<template>
  <div class="dict-manager">
    <!-- 操作栏 -->
    <div class="action-bar">
      <Button type="primary" icon="md-add" @click="handleAdd">
        新增参数
      </Button>
      <div class="search-box">
        <Input 
          v-model="searchText" 
          placeholder="搜索设备ID或参数名称..." 
          search 
          clearable
          style="width: 300px"
        />
      </div>
    </div>
    
    <!-- 数据表格 -->
    <div class="table-wrapper">
      <Table 
        :columns="columns" 
        :data="filteredData" 
        :loading="loading"
        stripe
        size="large"
      >
        <template slot-scope="{ row }" slot="action">
          <div class="action-buttons">
            <Button type="info" size="small" @click="handleEdit(row)" class="action-btn edit-btn">
              <Icon type="md-create" />
              <span>编辑</span>
            </Button>
            <Button type="error" size="small" @click="handleDelete(row)" class="action-btn delete-btn">
              <Icon type="md-trash" />
              <span>删除</span>
            </Button>
          </div>
        </template>
      </Table>
    </div>
    
    <!-- 新增/编辑对话框 -->
    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      :mask-closable="false"
      width="650"
      class-name="dict-modal"
    >
      <Form ref="form" :model="formData" :rules="formRules" :label-width="130">
        <FormItem label="设备ID" prop="deviceId">
          <Select 
            v-model="formData.deviceId" 
            placeholder="请选择设备"
            :disabled="isEdit"
          >
            <Option 
              v-for="device in deviceList" 
              :key="device.deviceId" 
              :value="device.deviceId"
            >
              {{ device.deviceId }} - {{ device.deviceNameCn }}
            </Option>
          </Select>
        </FormItem>
        <FormItem label="参数名称" prop="paramName">
          <Input 
            v-model="formData.paramName" 
            placeholder="请输入参数名称（英文）"
            :disabled="isEdit"
          />
        </FormItem>
        <FormItem label="参数单位" prop="paramUnit">
          <Input v-model="formData.paramUnit" placeholder="请输入参数单位（可为空）" />
        </FormItem>
        <FormItem label="参数中文名称" prop="paramNameCn">
          <Input v-model="formData.paramNameCn" placeholder="请输入参数中文名称" />
        </FormItem>
        <FormItem label="参数英文名称" prop="paramNameEn">
          <Input v-model="formData.paramNameEn" placeholder="请输入参数英文名称" />
        </FormItem>
      </Form>
      
      <div slot="footer">
        <Button @click="modalVisible = false">取消</Button>
        <Button type="primary" @click="handleSubmit" :loading="submitting">
          {{ isEdit ? '保存' : '新增' }}
        </Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import api from '@/api/index.js'

export default {
  name: 'ParamDict',
  data() {
    return {
      searchText: '',
      loading: false,
      tableData: [],
      deviceList: [],
      modalVisible: false,
      submitting: false,
      isEdit: false,
      formData: {
        id: null,
        deviceId: '',
        paramName: '',
        paramUnit: '',
        paramNameCn: '',
        paramNameEn: ''
      },
      formRules: {
        deviceId: [
          { required: true, message: '请选择设备', trigger: 'change' }
        ],
        paramName: [
          { required: true, message: '请输入参数名称', trigger: 'blur' }
        ],
        paramNameCn: [
          { required: true, message: '请输入参数中文名称', trigger: 'blur' }
        ],
        paramNameEn: [
          { required: true, message: '请输入参数英文名称', trigger: 'blur' }
        ]
      },
      columns: [
        {
          title: 'ID',
          key: 'id',
          width: 70,
          align: 'center'
        },
        {
          title: '设备ID',
          key: 'deviceId',
          minWidth: 120
        },
        {
          title: '参数名称',
          key: 'paramName',
          minWidth: 120
        },
        {
          title: '参数单位',
          key: 'paramUnit',
          width: 100,
          render: (h, params) => {
            return h('span', params.row.paramUnit || '--')
          }
        },
        {
          title: '参数中文名称',
          key: 'paramNameCn',
          minWidth: 150
        },
        {
          title: '参数英文名称',
          key: 'paramNameEn',
          minWidth: 150
        },
        {
          title: '操作',
          slot: 'action',
          width: 200,
          align: 'center'
        }
      ]
    }
  },
  computed: {
    modalTitle() {
      return this.isEdit ? '编辑参数字典' : '新增参数字典'
    },
    filteredData() {
      if (!this.searchText) {
        return this.tableData
      }
      const text = this.searchText.toLowerCase()
      return this.tableData.filter(item => 
        item.deviceId.toLowerCase().includes(text) ||
        item.paramName.toLowerCase().includes(text) ||
        item.paramNameCn.includes(text) ||
        item.paramNameEn.toLowerCase().includes(text)
      )
    }
  },
  mounted() {
    this.loadDeviceList()
    this.loadData()
  },
  methods: {
    loadDeviceList() {
      api.getData('system-server/monitor/dict/device/list', this).then(res => {
        if (res.state != 0) {
          this.deviceList = res.data || []
        }
      })
    },
    
    loadData() {
      this.loading = true
      api.getData('system-server/monitor/dict/param/list', this).then(res => {
        this.loading = false
        if (res.state != 0) {
          this.tableData = res.data || []
          this.$emit('count-change', this.tableData.length)
        } else {
          this.$Message.error(res.msg || '加载数据失败')
        }
      }).catch(() => {
        this.loading = false
        this.$Message.error('加载数据失败')
      })
    },
    
    handleAdd() {
      this.isEdit = false
      this.formData = {
        id: null,
        deviceId: '',
        paramName: '',
        paramUnit: '',
        paramNameCn: '',
        paramNameEn: ''
      }
      this.modalVisible = true
      this.$nextTick(() => {
        this.$refs.form.resetFields()
      })
    },
    
    handleEdit(row) {
      this.isEdit = true
      this.modalVisible = true
      this.$nextTick(() => {
        // Clear validation errors first
        this.$refs.form.resetFields()
        // Then set the form data
        this.formData = { ...row }
      })
    },
    
    handleDelete(row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除参数 "${row.deviceId} - ${row.paramName}" 吗？删除后无法恢复。`,
        onOk: () => {
          api.postData(`system-server/monitor/dict/param/delete?id=${row.id}`, {}, this).then(res => {
            if (res.state != 0) {
              this.$Message.success('删除成功')
              this.loadData()
            } else {
              this.$Message.error(res.msg || '删除失败')
            }
          }).catch(() => {
            this.$Message.error('删除失败')
          })
        }
      })
    },
    
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return
        }
        
        this.submitting = true
        const data = { ...this.formData }
        if (!this.isEdit) {
          delete data.id
        }
        
        api.postData('system-server/monitor/dict/param/save', data, this).then(res => {
          this.submitting = false
          if (res.state != 0) {
            this.$Message.success(this.isEdit ? '保存成功' : '新增成功')
            this.modalVisible = false
            this.loadData()
          } else {
            this.$Message.error(res.msg || '操作失败')
          }
        }).catch(() => {
          this.submitting = false
          this.$Message.error('操作失败')
        })
      })
    }
  }
}
</script>

<style lang="less" scoped>
@import './dict-common.less';
</style>
